/**
 * LeadQueries
 *
 * Responsabilidade: Operações de leitura (queries) de leads no Supabase.
 * Princípio: Single Responsibility + DRY — helpers eliminam duplicação.
 */

import { supabase } from '../../../lib/supabase';
import { Lead } from '../../../domain/entities/Lead';
import { InfrastructureError } from '../../../core/errors/DomainErrors';
import { LeadMapper } from '../mappers/LeadMapper';
import { LeadValidator } from '../validators/LeadValidator';

export class LeadQueries {
    private static readonly QUERY_STRING =
        '*, contact:contacts(name, email, whatsapp, phone), creator:profiles!opportunities_created_by_fkey(id, full_name)';
    private static readonly TABLE_NAME = 'opportunities';
    /** PostgREST limita ~1000 linhas por request; páginas além disso são obrigatórias. */
    private static readonly PAGE_SIZE = 1000;

    /**
     * Busca todas as negociações (abertas, ganhas e perdidas).
     * O filtro de status é feito no frontend via useKanbanFilters.
     */
    static async findAll(organizationId?: string): Promise<Lead[]> {
        return this.fetchPaginated(
            (from, to) =>
                this.buildQuery(organizationId)
                    .order('created_at', { ascending: true })
                    .range(from, to),
            'findAll',
            'Erro ao buscar oportunidades',
        );
    }

    static async findById(id: string): Promise<Lead | null> {
        try {
            LeadValidator.validateId(id, 'ID da oportunidade');

            const { data, error } = await supabase
                .from(this.TABLE_NAME)
                .select(this.QUERY_STRING)
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                this.logError('findById', error);
                throw new InfrastructureError(`Erro ao buscar lead: ${error.message}`, error);
            }

            return LeadMapper.toEntity(data);
        } catch (error) {
            return this.handleError(error, 'findById', 'Erro inesperado ao buscar lead');
        }
    }

    static async findByStage(stageId: string): Promise<Lead[]> {
        LeadValidator.validateId(stageId, 'ID do estágio');

        const query = supabase
            .from(this.TABLE_NAME)
            .select(this.QUERY_STRING)
            .eq('stage', stageId);

        return this.executeQuery(query, 'findByStage', 'Erro ao buscar oportunidades por estágio');
    }

    static async findByFunnel(funnelId: string, organizationId?: string): Promise<Lead[]> {
        try {
            LeadValidator.validateId(funnelId, 'ID do funil');

            const { data: stages, error: stageError } = await supabase
                .from('funnel_stages')
                .select('id')
                .eq('funnel_id', funnelId);

            if (stageError) {
                this.logError('findByFunnel', stageError);
                return [];
            }

            const stageIds = (stages || []).map((s) => s.id);
            if (stageIds.length === 0) return [];

            // Somente stage ∈ etapas do funil (não misturar funis; inclui won/lost da etapa).
            return this.fetchPaginated(
                (from, to) =>
                    this.buildQuery(organizationId)
                        .in('stage', stageIds)
                        .order('created_at', { ascending: true })
                        .range(from, to),
                'findByFunnel',
                'Erro ao buscar oportunidades do funil',
            );
        } catch (error) {
            if (error instanceof InfrastructureError) throw error;
            this.logError('findByFunnel', error);
            return [];
        }
    }

    static async findByAgent(agentName: string, organizationId?: string): Promise<Lead[]> {
        if (!agentName || agentName.trim().length === 0) return [];

        const query = this.buildQuery(organizationId).eq('assigned_to', agentName);
        return this.executeSafeQuery(query, 'findByAgent');
    }

    // --- Helpers públicos (usados por LeadStatsQueries) ---

    public static buildQuery(organizationId?: string): any {
        let query = supabase.from(this.TABLE_NAME).select(this.QUERY_STRING);
        if (organizationId) query = query.eq('organization_id', organizationId);
        return query;
    }

    public static buildCountQuery(organizationId?: string): any {
        let query = supabase.from(this.TABLE_NAME).select('*', { count: 'exact', head: true });
        if (organizationId) query = query.eq('organization_id', organizationId);
        return query;
    }

    public static async executeQuery(
        query: any,
        methodName: string,
        errorMessage: string,
    ): Promise<Lead[]> {
        try {
            const { data, error } = await query;
            if (error) {
                this.logError(methodName, error);
                throw new InfrastructureError(`${errorMessage}: ${error.message}`, error);
            }
            return this.mapRows(data);
        } catch (error) {
            return this.handleError(error, methodName, `Erro inesperado: ${errorMessage}`);
        }
    }

    public static async executeSafeQuery(query: any, methodName: string): Promise<Lead[]> {
        try {
            const { data, error } = await query;
            if (error) {
                this.logError(methodName, error);
                return [];
            }
            return this.mapRows(data);
        } catch (error) {
            this.logError(methodName, error);
            return [];
        }
    }

    public static async executeCountQuery(query: any, methodName: string): Promise<number> {
        try {
            const { count, error } = await query;
            if (error) {
                this.logError(methodName, error);
                return 0;
            }
            return count || 0;
        } catch (error) {
            this.logError(methodName, error);
            return 0;
        }
    }

    public static logError(methodName: string, error: any): void {
        console.error(`[LeadQueries.${methodName}] Database error:`, error);
    }

    public static handleError(error: any, methodName: string, message: string): never {
        if (error instanceof InfrastructureError) throw error;
        this.logError(methodName, error);
        throw new InfrastructureError(message);
    }

    /** Pagina até esgotar o resultado (evita corte silencioso em orgs com > PAGE_SIZE leads). */
    private static async fetchPaginated(
        buildPage: (from: number, to: number) => any,
        methodName: string,
        errorMessage: string,
    ): Promise<Lead[]> {
        const allRows: Lead[] = [];
        let offset = 0;

        while (true) {
            const batch = await this.executeQuery(
                buildPage(offset, offset + this.PAGE_SIZE - 1),
                methodName,
                errorMessage,
            );
            allRows.push(...batch);
            if (batch.length < this.PAGE_SIZE) break;
            offset += this.PAGE_SIZE;
        }

        return allRows;
    }

    private static mapRows(data: unknown): Lead[] {
        if (!Array.isArray(data)) return [];
        return data.map((row: unknown) => LeadMapper.toEntity(row));
    }
}
